import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import Loader from 'react-spinners/PulseLoader'
import { colors, institutionalColors } from '~/design'
import InsightsEmailIcon from '~/components/icons/icon-insights-email.svg'

export interface MonitorUserPermissionViewItem {
  key: string
  label: string
  description?: string
  type: string
  sectorKey?: string
  isEnabled: boolean
}

interface MonitorUserPermissionsSettingsProps {
  permissions: MonitorUserPermissionViewItem[]
  permissionStates: Record<string, boolean>
  isLoading: boolean
  isSaving: boolean
  isAdmin: boolean
  receiveWeeklyInsightsEmail: boolean
  hasChanges: boolean
  allPermissionsEnabled: boolean
  errorMessage?: string
  onRetry: () => void
  onToggleAllPermissions: () => void
  onTogglePermission: (permissionKey: string) => void
  onToggleReceiveWeeklyInsightsEmail: () => void
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void
}

const SettingsContainer = styled.form`
  display: flex;
  flex-direction: column;
  cursor: default;
`

const HeaderCard = styled.div`
  display: flex;
  border-radius: 8px 8px 0px 0px;
  background: ${colors.oceanBlue};
  margin: 0;
  padding: 20px;

  h2 {
    color: #fff;
    line-height: 22px;
    font-weight: 600;
    font-size: 18px;
    margin: 0;
  }
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
  color: ${institutionalColors.graySebrae30};
  background: #fff;
  min-height: calc(100vh - 250px);
  padding: 20px;
  gap: 20px;
  border: solid 1px rgba(221, 221, 221, 1);
  border-radius: 0px 0px 8px 8px;
  box-shadow: 0px 32px 30px 0px rgba(20, 46, 82, 0.2);
`

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 280px;
`

const DescriptionText = styled.p`
  margin: 0;
  line-height: 1.5;
`

const SearchField = styled.input`
  width: 100%;
  min-height: 44px;
  padding: 10px 14px;
  border: 1px solid #d6deea;
  border-radius: 8px;
  background: #fff;
  color: ${institutionalColors.graySebrae30};
  font-size: 15px;
  line-height: 1.4;

  &::placeholder {
    color: #7b8794;
  }

  &:focus {
    outline: none;
    border-color: ${colors.oceanBlue};
    box-shadow: 0 0 0 3px rgba(0, 94, 184, 0.12);
  }
`

const SearchSummaryText = styled.small`
  color: #5b6b7c;
  line-height: 1.4;
`

const NoticeCard = styled.div`
  padding: 14px 16px;
  border-radius: 8px;
  background: #f0f6fe;
  color: ${colors.oceanBlue};
  border: 1px solid rgba(15, 67, 138, 0.14);
  line-height: 1.5;
`

const WeeklyInsightsEmailCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 22px;
  padding: 26px 32px;
  border-radius: 8px;
  border: 1px solid #cfe0f4;
  background: #f0f7ff;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    padding: 20px;
  }
`

const WeeklyInsightsEmailInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 22px;
  min-width: 0;

  svg {
    flex: 0 0 auto;
    width: 64px;
    height: 64px;
    color: ${colors.oceanBlue};
  }

  strong,
  p {
    margin: 0;
  }

  strong {
    display: block;
    color: #0f438a;
    font-size: 25px;
    font-weight: 700;
    line-height: 1.2;
  }

  p {
    margin-top: 2px;
    color: #4d5f78;
    font-size: 18px;
    line-height: 1.35;
  }

  @media (max-width: 768px) {
    align-items: flex-start;
    gap: 14px;

    svg {
      width: 46px;
      height: 46px;
    }

    strong {
      font-size: 20px;
    }

    p {
      font-size: 15px;
    }
  }
`

const BulkActionCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #d6deea;
  background: #f8fbff;

  strong,
  p {
    margin: 0;
  }

  p {
    margin-top: 4px;
    color: #5b6b7c;
    line-height: 1.4;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`

const GroupSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const GroupTitle = styled.h3`
  margin: 0;
  color: ${institutionalColors.graySebrae30};
`

const PermissionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const PermissionCard = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #d6deea;
  background: #fff;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const PermissionInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;

  strong {
    color: ${institutionalColors.graySebrae30};
    font-size: 16px;
    line-height: 1.4;
  }

  p,
  small {
    margin: 0;
  }

  small {
    color: #5b6b7c;
    line-height: 1.4;
    word-break: break-word;
  }
`

const ToggleButton = styled.button<{
  $isEnabled: boolean
}>`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  align-self: center;
  min-width: 150px;
  justify-content: flex-end;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;

  .label {
    font-size: 15px;
    font-weight: 700;
    color: ${props => (props.$isEnabled ? colors.oceanBlue : '#66788a')};
  }

  @media (max-width: 768px) {
    align-self: flex-end;
  }
`

const ToggleTrack = styled.span<{
  $isEnabled: boolean
}>`
  position: relative;
  display: inline-flex;
  width: 52px;
  height: 30px;
  border-radius: 999px;
  background: ${props => (props.$isEnabled ? colors.oceanBlue : '#ced8e2')};
  transition: background 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: ${props => (props.$isEnabled ? '25px' : '3px')};
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0px 2px 4px 0px rgba(20, 46, 82, 0.2);
    transition: left 0.2s ease;
  }
`

const WeeklyInsightsEmailToggleButton = styled(ToggleButton)`
  min-width: 174px;

  .label {
    color: ${colors.oceanBlue};
    font-size: 17px;
  }

  @media (max-width: 768px) {
    align-self: flex-end;
  }
`

const WeeklyInsightsEmailToggleTrack = styled(ToggleTrack)<{
  $isEnabled: boolean
}>`
  width: 58px;
  height: 32px;

  &::after {
    top: 3px;
    left: ${props => (props.$isEnabled ? '29px' : '3px')};
    width: 26px;
    height: 26px;
  }
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 8px;
`

const SaveButton = styled.button`
  background-color: #005eb8;
  box-shadow: 0px 2px 4px 0px #142e5233;
  border: none;
  border-radius: 4px;
  padding: 6px 18px;
  font-weight: 700;
  font-size: 16px;
  line-height: 23px;
  color: #fff;
  cursor: pointer;

  &:hover:enabled {
    background-color: #0f438a;
    box-shadow: 0px 4px 8px 0px #142e5233;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const RetryButton = styled.button`
  align-self: flex-start;
  background: transparent;
  border: 1px solid ${colors.oceanBlue};
  border-radius: 4px;
  padding: 8px 14px;
  color: ${colors.oceanBlue};
  cursor: pointer;
  font-weight: 600;
`

const groupTitlesByKey: Record<string, string> = {
  monitorServiceOverview: 'Visão Geral de Atendimentos',
  monitorOnlineCourses: 'Cursos Online',
  monitorAppSebrae: 'App SEBRAE',
  monitorEmissorNfe: 'Emissor NF-e',
  monitorConecta: 'Conecta',
  monitorPortfolio: 'Portfólios',
  monitorPortfolioOutOfNetwork: 'Fora dos Portfólios',
  standalone: 'Produtos e Programas',
}

const groupOrder = [
  'monitorServiceOverview',
  'monitorOnlineCourses',
  'monitorAppSebrae',
  'monitorEmissorNfe',
  'monitorConecta',
  'monitorPortfolio',
  'monitorPortfolioOutOfNetwork',
  'standalone',
]

const getPermissionGroupKey = (permission: MonitorUserPermissionViewItem) =>
  permission.sectorKey || 'standalone'

const getPermissionGroupTitle = (groupKey: string) => groupTitlesByKey[groupKey] || groupKey

const getPermissionSortText = (permission: MonitorUserPermissionViewItem) =>
  permission.description || permission.label || permission.key

const normalizeSearchText = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()

const orderPermissions = (permissions: MonitorUserPermissionViewItem[]) =>
  [...permissions].sort((permission0, permission1) => {
    const groupKey0 = getPermissionGroupKey(permission0)
    const groupKey1 = getPermissionGroupKey(permission1)
    const groupOrder0 = groupOrder.indexOf(groupKey0)
    const groupOrder1 = groupOrder.indexOf(groupKey1)
    const normalizedGroupOrder0 = groupOrder0 === -1 ? Number.MAX_SAFE_INTEGER : groupOrder0
    const normalizedGroupOrder1 = groupOrder1 === -1 ? Number.MAX_SAFE_INTEGER : groupOrder1

    if (groupOrder0 !== groupOrder1) {
      return normalizedGroupOrder0 - normalizedGroupOrder1
    }

    return getPermissionSortText(permission0).localeCompare(
      getPermissionSortText(permission1),
      'pt-BR',
    )
  })

export default function MonitorUserPermissionsSettings({
  permissions,
  permissionStates,
  isLoading,
  isSaving,
  isAdmin,
  receiveWeeklyInsightsEmail,
  hasChanges,
  allPermissionsEnabled,
  errorMessage,
  onRetry,
  onToggleAllPermissions,
  onTogglePermission,
  onToggleReceiveWeeklyInsightsEmail,
  onSubmit,
}: MonitorUserPermissionsSettingsProps) {
  const [searchValue, setSearchValue] = useState('')

  const filteredPermissions = useMemo(() => {
    const normalizedSearchValue = normalizeSearchText(searchValue)

    if (!normalizedSearchValue) {
      return permissions
    }

    return permissions.filter(permission => {
      const groupTitle = getPermissionGroupTitle(getPermissionGroupKey(permission))
      const searchableFields = [
        permission.label,
        permission.description,
        permission.key,
        groupTitle,
      ]

      return searchableFields.some(field =>
        normalizeSearchText(field || '').includes(normalizedSearchValue),
      )
    })
  }, [permissions, searchValue])

  const groupedPermissions = useMemo(() => {
    const groupedItems = new Map<string, MonitorUserPermissionViewItem[]>()

    for (const permission of orderPermissions(filteredPermissions)) {
      const groupKey = getPermissionGroupKey(permission)
      const currentGroup = groupedItems.get(groupKey) || []
      currentGroup.push(permission)
      groupedItems.set(groupKey, currentGroup)
    }

    return Array.from(groupedItems.entries()).map(([groupKey, items]) => ({
      groupKey,
      title: getPermissionGroupTitle(groupKey),
      items,
    }))
  }, [filteredPermissions])

  return (
    <SettingsContainer onSubmit={onSubmit}>
      {!isLoading && !errorMessage ? (
        <WeeklyInsightsEmailCard>
          <WeeklyInsightsEmailInfo>
            <InsightsEmailIcon />

            <div>
              <strong>Quero receber insights semanais por email</strong>
              <p>Receba um resumo dos seus principais insights toda segunda-feira.</p>
            </div>
          </WeeklyInsightsEmailInfo>

          <WeeklyInsightsEmailToggleButton
            type='button'
            $isEnabled={receiveWeeklyInsightsEmail}
            aria-label='Ativar ou desativar recebimento de insights semanais por email'
            aria-pressed={receiveWeeklyInsightsEmail}
            onClick={onToggleReceiveWeeklyInsightsEmail}
          >
            <span className='label'>{receiveWeeklyInsightsEmail ? 'Receber' : 'Não receber'}</span>
            <WeeklyInsightsEmailToggleTrack $isEnabled={receiveWeeklyInsightsEmail} />
          </WeeklyInsightsEmailToggleButton>
        </WeeklyInsightsEmailCard>
      ) : null}

      <HeaderCard>
        <h2>Tópicos disponíveis</h2>
      </HeaderCard>

      <ContentContainer>
        {isLoading ? (
          <LoaderContainer>
            <Loader color={colors.oceanBlue} />
          </LoaderContainer>
        ) : errorMessage ? (
          <>
            <DescriptionText>{errorMessage}</DescriptionText>
            <RetryButton type='button' onClick={onRetry}>
              Tentar novamente
            </RetryButton>
          </>
        ) : (
          <>
            <DescriptionText>Ative ou desative os tópicos de interesse.</DescriptionText>

            <SearchField
              type='search'
              value={searchValue}
              placeholder='Buscar tópico por nome ou descrição'
              onChange={event => setSearchValue(event.target.value)}
            />

            <SearchSummaryText>
              {filteredPermissions.length} de {permissions.length} tópicos exibidos
            </SearchSummaryText>

            {isAdmin ? (
              <NoticeCard>
                Seu usuário possui acesso administrativo. Os tópicos abaixo continuam salvos para
                consulta e personalização do seu vínculo granular.
              </NoticeCard>
            ) : null}

            <BulkActionCard>
              <div>
                <strong>Permitir tudo</strong>
                <p>Ativa ou desativa todos os tópicos da lista antes de salvar.</p>
              </div>

              <ToggleButton
                type='button'
                $isEnabled={allPermissionsEnabled}
                aria-pressed={allPermissionsEnabled}
                onClick={onToggleAllPermissions}
              >
                <span className='label'>{allPermissionsEnabled ? 'Ativado' : 'Desativado'}</span>
                <ToggleTrack $isEnabled={allPermissionsEnabled} />
              </ToggleButton>
            </BulkActionCard>

            {groupedPermissions.length === 0 ? (
              <NoticeCard>Nenhuma permissão encontrada para o filtro informado.</NoticeCard>
            ) : (
              groupedPermissions.map(group => (
                <GroupSection key={group.groupKey}>
                  <GroupTitle>{group.title}</GroupTitle>

                  <PermissionList>
                    {group.items.map(permission => {
                      const isEnabled = permissionStates[permission.key] ?? false

                      return (
                        <PermissionCard key={permission.key}>
                          <PermissionInfo>
                            <strong>{permission.label}</strong>
                            {permission.description ? <p>{permission.description}</p> : null}
                          </PermissionInfo>

                          <ToggleButton
                            type='button'
                            $isEnabled={isEnabled}
                            aria-pressed={isEnabled}
                            onClick={() => onTogglePermission(permission.key)}
                          >
                            <span className='label'>{isEnabled ? 'Ativado' : 'Desativado'}</span>
                            <ToggleTrack $isEnabled={isEnabled} />
                          </ToggleButton>
                        </PermissionCard>
                      )
                    })}
                  </PermissionList>
                </GroupSection>
              ))
            )}

            <ButtonWrapper>
              <SaveButton type='submit' disabled={isSaving || isLoading || !hasChanges}>
                {isSaving ? 'Salvando...' : 'Salvar'}
              </SaveButton>
            </ButtonWrapper>
          </>
        )}
      </ContentContainer>
    </SettingsContainer>
  )
}
